import { Deferred, Head, router } from "@inertiajs/react"
import { AlertTriangle, CalendarClock } from "lucide-react"
import { useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { MissionCard } from "@/components/mission-card"
import { CountdownHero } from "@/components/mission-drop/countdown-hero"
import { DropSkeleton } from "@/components/mission-drop/drop-skeleton"
import { NotifyToggle } from "@/components/mission-drop/notify-toggle"
import { RevealBanner } from "@/components/mission-drop/reveal-banner"
import { Button } from "@/components/ui/button"
import { useCurrentContext } from "@/hooks/use-current-context"
import AppLayout from "@/layouts/app-layout"
import { MOTION_BEATS } from "@/lib/motion"
import type { Collab } from "@/types/collab"
import type { MissionDropProp } from "@/types/mission-drop"

/** How often the countdown screen silently refetches to correct drift + catch
 *  a drop that landed while the tab was backgrounded (UX Spec §4). */
const DRIFT_REFETCH_MS = 30_000

/**
 * Creator/MissionDrop — §07 Mission Drops (UX Spec). The transient
 * countdown→reveal "drop moment" for the active brand's single relevant drop.
 *
 * The `drop` prop is Inertia-deferred: the `DropSkeleton` loading state paints
 * immediately, then the resolved drop streams in (UX Spec §3). The brand strip
 * is rendered once by the creator shell (OKS-701), not per page.
 * The inner `MissionDropContent` holds every drop-dependent hook, so it only
 * mounts once `drop` is present.
 */
export default function CreatorMissionDrop({
  drop,
}: {
  drop?: MissionDropProp
}) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("mission_drop.head_title")} />
      <div className="flex flex-col gap-4 p-4" data-testid="mission-drop-page">
        {/* OKS-701 — brand strip now lives in the creator shell (below the
            navbar on every creator page), so this page no longer renders it. */}
        <Deferred data="drop" fallback={<DropSkeleton />}>
          {drop ? <MissionDropContent drop={drop} /> : null}
        </Deferred>
      </div>
    </AppLayout>
  )
}

/**
 * The resolved §07 content — four states driven by the self-describing `drop`
 * prop (empty / error / countdown / revealed).
 *
 * In-place flip: while in `countdown` it (a) lets `CountdownHero` reload
 * `drop` at zero, and (b) drift-refetches every {@link DRIFT_REFETCH_MS}. The
 * server then returns `revealed` (state derived from `drops_at` vs now, NOT
 * the reveal job), the locked teasers become live cards, and focus moves to
 * the reveal heading. The job's `CreatorChannel.refetch(["drop", …])` reloads
 * this same prop for creators who are watching.
 */
function MissionDropContent({ drop }: { drop: MissionDropProp }) {
  const { t } = useTranslation()
  const { currentBrand } = useCurrentContext()

  const reloadDrop = useCallback(() => {
    router.reload({ only: ["drop"] })
  }, [])

  // Drift-correcting refetch loop — countdown only.
  useEffect(() => {
    if (drop.state !== "countdown") return
    const id = setInterval(reloadDrop, DRIFT_REFETCH_MS)
    return () => clearInterval(id)
  }, [drop.state, reloadDrop])

  if (drop.state === "error") {
    return (
      <EmptyState
        icon={AlertTriangle}
        role="status"
        title={t("mission_drop.error.title")}
        data-testid="mission-drop-error"
        actions={
          <Button onClick={reloadDrop} data-testid="mission-drop-retry">
            {t("mission_drop.error.retry")}
          </Button>
        }
      />
    )
  }

  if (drop.state === "empty") {
    return (
      <EmptyState
        icon={CalendarClock}
        role="status"
        title={t("mission_drop.empty.title")}
        description={t("mission_drop.empty.body", {
          brand: currentBrand?.name ?? "",
        })}
        data-testid="mission-drop-empty"
      />
    )
  }

  if (drop.state === "countdown" && drop.drops_at) {
    return (
      <div className="flex flex-col gap-4" data-testid="mission-drop-countdown">
        <CountdownHero
          dropsAt={drop.drops_at}
          campaignLabel={drop.campaign_label ?? ""}
          missionCount={drop.mission_count ?? 0}
          onComplete={reloadDrop}
        >
          <NotifyToggle enabled={drop.notify_enabled ?? true} />
        </CountdownHero>

        <p
          className="text-muted-foreground text-xs font-bold tracking-[0.12em] uppercase"
          data-testid="mission-drop-section-label"
        >
          {t("mission_drop.landing")}
        </p>

        <DropMissionList missions={drop.missions} animate={false} />
      </div>
    )
  }

  if (drop.state === "revealed") {
    return (
      <div className="flex flex-col gap-4" data-testid="mission-drop-revealed">
        {/* Banner mounts on entering `revealed` (whether via the in-place flip
            or a direct load from the drop notification) — it takes focus on
            mount so SR announces the celebratory state (UX Spec §6). */}
        <RevealBanner campaignLabel={drop.campaign_label ?? ""} focusOnMount />
        <p
          className="text-muted-foreground text-sm font-medium"
          data-testid="mission-drop-reveal-caption"
        >
          {t("mission_drop.reveal.social_proof")}
        </p>
        <DropMissionList missions={drop.missions} animate />
      </div>
    )
  }

  return null
}

/**
 * The drop's MissionCards. `mission.locked` (server-derived) drives the
 * per-card lock; on the reveal, cards play the one-time `drop-reveal` entrance
 * (the "live-pulse" flip — CSS-only, reduced-motion-safe).
 */
function DropMissionList({
  missions,
  animate,
}: {
  missions?: Collab[]
  animate: boolean
}) {
  if (!missions || missions.length === 0) return null

  return (
    <ul className="flex flex-col gap-3" data-testid="mission-drop-list">
      {missions.map((collab) => (
        <li
          key={collab.id}
          className={animate ? MOTION_BEATS["drop-reveal"] : undefined}
        >
          <MissionCard
            collab={collab}
            locked={Boolean(collab.mission.locked)}
          />
        </li>
      ))}
    </ul>
  )
}
